"use client";

import { useState, useEffect, useRef } from "react";

interface VideoCardData {
  title: string;
  description: string;
  video: string;
  category: string;
}

interface VideoCardProps {
  item: VideoCardData;
}

export function VideoCard({ item }: VideoCardProps) {
  const [isClient, setIsClient] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Ensure component only runs on client side to prevent hydration mismatch
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Handle video loading and autoplay
  useEffect(() => {
    if (isClient && videoRef.current) {
      const video = videoRef.current;

      // Preload the video
      video.preload = "auto";

      // Handle video load event
      const handleLoadedData = () => {
        setIsVideoLoaded(true);
        // Start playing once loaded
        video.play().catch(() => {
          // Autoplay failed, which is normal in some browsers
        });
      };

      video.addEventListener("loadeddata", handleLoadedData);

      // Start loading immediately
      video.load();

      return () => {
        video.removeEventListener("loadeddata", handleLoadedData);
      };
    }
  }, [isClient]);

  return (
    <div
      className="group relative cursor-pointer transition-all duration-500 ease-out hover:scale-105 hover:shadow-2xl"
      aria-label={`View ${item.title} transformation`}
    >
      {/* Card Container - Portrait Height (3:5) */}
      <div className="relative w-full aspect-[3/5] bg-gradient-to-br from-muted/20 to-muted/40 rounded-2xl overflow-hidden backdrop-blur-sm">
        {/* Video Background */}
        {isClient ? (
          <video
            ref={videoRef}
            className="w-full h-full object-cover transition-opacity duration-500"
            muted
            loop
            playsInline
            preload="auto"
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            style={{ opacity: isVideoLoaded ? 1 : 0.8 }}
          >
            <source src={item.video} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className="w-full h-full object-cover bg-cover bg-center" />
        )}

        {/* Loading indicator */}
        {isClient && !isVideoLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />

        {/* Category Text - Only visible on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <h3 className="text-white text-2xl sm:text-3xl font-bold text-center px-4">
            {item.title}
          </h3>
        </div>

        {/* Hover Effect Overlay */}
        <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-500" />
      </div>
    </div>
  );
}
